import { RegistrationProcess, Resend } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BasicLink } from '@components/buttons/BasicLink/BasicLink.component';
import { PlcFlatRectLoader } from '@components/Loaders/PlcFlatRectLoader.component';
import { PlcMessage } from '@components/PlcMessage/PlcMessage.component';
import { Text } from '@components/Text/Text.component';
import { PlcPageHeader } from '@layout/components/PlcPageHeader/PlcPageHeader.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { PlcDispatch, RootState } from '@store/store';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';

type Props = ComponentPropsWithRef<'main'> & {
  isMobile: boolean;
  onNewMemberProfileClick?: () => void;
  resend: Resend;
};

const SignUpConfirmationLayout: FC<Props> = ({ className, isMobile, onNewMemberProfileClick, resend, ...rest }) => {
  const {
    user: { resendActivationLink },
  } = useDispatch<PlcDispatch>();
  const resendActivationLinkLoading = useSelector(
    (state: RootState) => state.loading.effects.user.resendActivationLink
  );
  const { errorCode } = useSelector((state: RootState) => state.user);
  const [isSuccess, setIsSuccess] = useState(false);
  const { t } = useTranslation(['auth']);
  const { email, registrationProcess } = resend;

  function handleSendEmailAgain() {
    resendActivationLink(resend)
      .then(() => setIsSuccess(true))
      .catch(() => setIsSuccess(false));
  }

  const rootClass = classnames('sign-up-layout', 'plc-page', className);
  const subtitleKey = `auth.sign-up.confirmation.${
    registrationProcess === RegistrationProcess.Regular ? 'member' : 'pro'
  }.sub-title`;
  const bodyKey = `auth.sign-up.confirmation.body.${registrationProcess === RegistrationProcess.Sec ? 'sec' : 'part1'}`;

  return (
    <main className={rootClass} {...rest}>
      <PlcPageHeader heroImgSrc="inscription" i18nTitle="auth.sign-up.title" />
      {resendActivationLinkLoading ? (
        <PlcFlatRectLoader height={200} uniqueKey="activationLink" width={100} />
      ) : (
        <section className="wrapper-s main-section">
          <Text className="plc-paragraph" tag="p" variant="h3">
            <Trans i18nKey={subtitleKey} ns="auth">
              Check email
              <Text flavour="yellow" tag="span" variant="h3">
                Pro
              </Text>
            </Trans>
          </Text>
          {(errorCode || isSuccess) && (
            <PlcMessage className="plc-paragraph" errorCode={errorCode} flavour={isSuccess ? 'green' : 'red'}>
              {isSuccess ? t('auth.sign-up.resend-activation-email.confirm') : undefined}
            </PlcMessage>
          )}
          <Text className="plc-paragraph" variant="p">
            <Trans i18nKey={bodyKey} ns="auth" tOptions={{ email }}>
              To finalize <strong>email</strong>
            </Trans>
          </Text>
          <Text className="plc-paragraph" variant="small">
            <Trans i18nKey="auth.sign-up.confirmation.body.part2" ns="auth">
              If not received
              <BasicLink isUnderlined onClick={handleSendEmailAgain} textProps={{ variant: 'small' }}>
                send again
              </BasicLink>
              .
            </Trans>
          </Text>
          <Text className="plc-paragraph" variant="small">
            <Trans i18nKey="auth.sign-up.confirmation.body.part3" ns="auth">
              A question ? Contact us using our
              <BasicLink href={urlEnum.contactUs} isUnderlined textProps={{ variant: 'small' }}>
                contact form
              </BasicLink>
              .
              <br />
            </Trans>
          </Text>
        </section>
      )}
    </main>
  );
};

export { SignUpConfirmationLayout };
